import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { usePlayground } from "../../playground-store";

import { HydrationState } from "./useHydrateSession";

import { useUpdatePlaygroundSession } from "@/queries";
import { useAuth } from "@/context/auth-context";

const useIntializeSessionOnMount = (hydrationState: HydrationState) => {
  const userToken = useAuth()?.userToken || "";
  const { sessionId } = useParams();
  const currentPath = window.location.pathname;
  const isPlaygroundPath = currentPath.includes("playground");
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = usePlayground();
  const queryClient = useQueryClient();

  const updatePlaygroundSession = useUpdatePlaygroundSession(userToken, {
    onSuccess: () => {
      if (!sessionId && isPlaygroundPath && window.location.pathname === currentPath) {
        try {
          const pathWithoutInstance = currentPath.replace(new RegExp(`/${instanceId}/?$`), "");

          const baseUrl = pathWithoutInstance.endsWith("/")
            ? `${pathWithoutInstance}${instanceId}`
            : `${pathWithoutInstance}/${instanceId}`;

          if (window.location.pathname !== baseUrl) {
            setTimeout(() => {
              navigate(`${baseUrl}${location.search}`, { replace: true });
            }, 0);
          }
        } catch {
          if (!window.location.pathname.includes(instanceId)) {
            navigate(`${currentPath}/${instanceId}${location.search}`, {
              replace: true,
            });
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ["runGroups"] });
    }, // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError: () => {},
  });
  const sessionInitialized = useRef(false);
  const isRunning = useRef(false);
  const playground = usePlayground();

  useEffect(() => {
    isRunning.current = false;
    sessionInitialized.current = false;
    updatePlaygroundSession.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (
      sessionInitialized.current ||
      isRunning.current ||
      playground.isPendingSessionInitialization ||
      !isPlaygroundPath
    ) {
      return;
    }

    if (hydrationState !== HydrationState.ERROR && sessionId) {
      return;
    }

    isRunning.current = true;
    playground.setInitializing(true);

    const chatProps = {
      mode: playground.playgroundSessionMode,
      ...(playground.isSessionModeChat ? { chat_settings: playground.chat_settings } : {}),
    };

    updatePlaygroundSession.mutate(
      {
        workspaceId: playground.workspace_id,
        session: {
          id: playground.instanceId,
          name: playground.sessionName ?? "",
          prompt_blueprint: playground.blueprint,
          input_variables: playground.input_variable_sets.map(set => set.variables),
          input_variable_set_names: playground.input_variable_set_names,
          request_log_ids: playground.request_log_ids,
          source: playground.source,
          ...chatProps,
        },
      },
      {
        onSuccess: data => {
          sessionInitialized.current = true;
          // Apply chat_settings from the backend response — the backend may
          // have enriched them (e.g. injection_variable set from a detected
          // placeholder variable in the prompt template).
          if (data?.playground_session?.chat_settings) {
            playground.setChatSettings(data.playground_session.chat_settings);
          }
          playground.setSessionUpdatedAt(data.playground_session.updated_at);
          playground.setIsPendingSessionInitialization(false);
          setTimeout(() => {
            playground.setInitializing(false);
          }, 100);
        },
        onError: () => {
          setTimeout(() => {
            playground.setInitializing(false);
          }, 100);
        },
      },
    );
  }, [playground, updatePlaygroundSession, hydrationState, sessionId, isPlaygroundPath]);
};

export default useIntializeSessionOnMount;
